import {
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  MenuList,
  Skeleton,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePrefersReducedMotion } from '@omnea-app/fe-browser-utils/usePrefersReducedMotion'
import { CheckOMIcon, OpenRectArrowOutOMIcon, SearchOMIcon, SettingsOMIcon } from '@omnea-app/icons'
import { Popover, PopoverBody } from '@omnea-app/omui/Popover'
import { scrollFadeMask } from '@omnea-app/omui/scrollFadeMask'
import { OrgIcon } from './OrgIcon'
import type { ElementType, KeyboardEvent, ReactNode } from 'react'

export type OrgMenuOrg = {
  id: string
  name: string
  /** The signed-in user's role in this organisation, as shown to them. */
  role: string
  /** The organisation's square icon. */
  iconSrc?: string
  qa?: boolean
}

type OrgMenuProps = {
  /** Lets the toggle that opens the menu point `aria-controls` at it. */
  id?: string
  open: boolean
  anchorEl: HTMLElement | null
  onClose: () => void
  userName: string
  userEmail: string
  /** The signed-in user's 32px avatar. */
  avatar: ReactNode
  /** Left out while they load, which shows placeholder rows in their place. */
  orgs?: OrgMenuOrg[]
  currentOrgId: string
  /** Called with another organisation's id; picking the current one only closes the menu. */
  onSwitchOrg: (orgId: string) => void
  /** The user's settings page. Without it, the settings button is left out. */
  settingsHref?: string
  /** Renders `settingsHref`, e.g. Next's `Link`, so navigating stays client-side. */
  LinkComponent?: ElementType
  onLogOut: () => void
}

// Two organisations fit at a glance; past that the list gets a search box, as in the design.
const SEARCH_FROM = 3

// Same inset as `PopoverBody`, so the name lines up with the search and the organisations beneath it.
const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
}))

const AvatarSlot = styled('span')({
  display: 'flex',
  flexShrink: 0,
})

const User = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
})

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
}))

const FADE = '1.5rem'

const Orgs = styled(MenuList)({
  // As many organisations as the design shows before the list scrolls. In a short window the cap leaves room for the
  // header and search below the toggle: a panel that doesn't fit slides up over the toggle and scrolls as a whole.
  maxHeight: 'min(21rem, calc(100vh - 13rem))',
  overflowY: 'auto',
})

const Trailing = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginLeft: theme.spacing(2),
}))

// Sits where the first organisation would, in line with the rows' icons.
const NoResults = styled(Typography)(({ theme }) => ({
  display: 'block',
  padding: theme.spacing(2),
}))

const LOADING_ROWS = 2

// Laid out like an organisation's row, so the list doesn't shift when the organisations arrive.
const LoadingRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(0.5, 2),
}))

const matches = (org: OrgMenuOrg, query: string) => org.name.toLocaleLowerCase().includes(query)

/**
 * The account menu the top bar's `OrgToggle` opens: who is signed in, with their settings and log out, and
 * the organisations they can switch to. Past two organisations it adds a search box.
 */
export const OrgMenu = ({ id, open, anchorEl, onClose, ...content }: OrgMenuProps) => {
  const { t } = useTranslation('common')
  // The popover grows in on a timer that a CSS media query can't reach.
  const reduceMotion = usePrefersReducedMotion()

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      transitionDuration={reduceMotion ? 0 : 'auto'}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { role: 'dialog', 'aria-label': t('org-menu.label'), sx: { width: '20rem', mt: 1.5 } } }}
    >
      <OrgMenuContent onClose={onClose} {...content} />
    </Popover>
  )
}

// Separate from the popover so its search resets each time the menu opens: the popover unmounts it on close.
const OrgMenuContent = ({
  onClose,
  userName,
  userEmail,
  avatar,
  orgs,
  currentOrgId,
  onSwitchOrg,
  settingsHref,
  LinkComponent,
  onLogOut,
}: Omit<OrgMenuProps, 'id' | 'open' | 'anchorEl'>) => {
  const { t } = useTranslation('common')
  const [search, setSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  const isLoading = orgs == null
  const hasSearch = (orgs?.length ?? 0) >= SEARCH_FROM
  const query = search.trim().toLocaleLowerCase()
  const shown = query === '' ? (orgs ?? []) : (orgs ?? []).filter((org) => matches(org, query))

  const measureScroll = useCallback((list: HTMLElement) => {
    setCanScrollUp(list.scrollTop > 0)
    setCanScrollDown(list.scrollTop + list.clientHeight < list.scrollHeight - 1)
  }, [])

  useLayoutEffect(() => {
    const list = listRef.current
    if (list == null) return
    measureScroll(list)
    // The list's height follows the window's, so it can gain or lose its overflow without a scroll.
    const observer = new ResizeObserver(() => measureScroll(list))
    observer.observe(list)
    return () => observer.disconnect()
  }, [measureScroll, query, isLoading])

  const pick = (orgId: string) => {
    onClose()
    if (orgId !== currentOrgId) onSwitchOrg(orgId)
  }

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      listRef.current?.querySelector<HTMLElement>('[role="menuitemradio"]')?.focus()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const [first] = shown
      if (query !== '' && first != null) pick(first.id)
    }
  }

  // MenuList moves focus between the rows; above the first one, it hands back to the search box.
  const onListKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'ArrowUp' && hasSearch && event.target === listRef.current?.firstElementChild) {
      event.preventDefault()
      searchRef.current?.focus()
    }
  }

  return (
    <>
      <Header>
        {/* The name beside it already says who this is. */}
        <AvatarSlot aria-hidden>{avatar}</AvatarSlot>
        <User>
          <Typography variant="body-medium" noWrap>
            {userName}
          </Typography>
          <Typography variant="small-regular" color="text.muted" noWrap>
            {userEmail}
          </Typography>
        </User>
        <Actions>
          {settingsHref != null && (
            <Tooltip title={t('header.settings')}>
              <IconButton href={settingsHref} LinkComponent={LinkComponent} onClick={onClose}>
                <SettingsOMIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('header.logout')}>
            <IconButton onClick={onLogOut}>
              <OpenRectArrowOutOMIcon />
            </IconButton>
          </Tooltip>
        </Actions>
      </Header>
      <Divider />
      <PopoverBody>
        {hasSearch && (
          <TextField
            size="small"
            fullWidth
            autoFocus
            inputRef={searchRef}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('org-menu.search-placeholder')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOMIcon aria-hidden />
                  </InputAdornment>
                ),
              },
              htmlInput: { 'aria-label': t('org-menu.search-label'), onKeyDown: onSearchKeyDown },
            }}
          />
        )}
        {isLoading ? (
          <div role="status" aria-label={t('org-menu.loading')}>
            {Array.from({ length: LOADING_ROWS }, (_, index) => (
              <LoadingRow key={index} aria-hidden>
                <Skeleton variant="rounded" width="2rem" height="2rem" />
                <Stack flex={1}>
                  <Typography variant="body-regular">
                    <Skeleton width="60%" />
                  </Typography>
                  <Typography variant="small-regular">
                    <Skeleton width="30%" />
                  </Typography>
                </Stack>
              </LoadingRow>
            ))}
          </div>
        ) : shown.length === 0 ? (
          // With nothing typed there's no search to report on, such as when the organisations couldn't load.
          query !== '' && (
            <NoResults variant="body-regular" color="text.muted" role="status">
              {t('org-menu.no-results')}
            </NoResults>
          )
        ) : (
          <Orgs
            ref={listRef}
            aria-label={t('org-menu.organisations')}
            autoFocusItem={!hasSearch}
            disableListWrap
            onKeyDown={onListKeyDown}
            onScroll={(event) => measureScroll(event.currentTarget)}
            sx={{
              maskImage: scrollFadeMask({
                start: canScrollUp ? FADE : '0px',
                end: canScrollDown ? FADE : '0px',
                fadeSize: FADE,
                direction: 'to bottom',
              }),
            }}
          >
            {shown.map((org) => (
              <MenuItem key={org.id} role="menuitemradio" aria-checked={org.id === currentOrgId} onClick={() => pick(org.id)}>
                <OrgIcon name={org.name} src={org.iconSrc} />
                <ListItemText
                  primary={org.name}
                  secondary={org.role}
                  slotProps={{
                    primary: { variant: 'body-regular', noWrap: true },
                    secondary: { variant: 'small-regular', color: 'text.muted', noWrap: true },
                  }}
                />
                {(org.qa || org.id === currentOrgId) && (
                  <Trailing>
                    {org.qa && <Chip label={t('qa')} />}
                    {org.id === currentOrgId && <CheckOMIcon aria-hidden />}
                  </Trailing>
                )}
              </MenuItem>
            ))}
          </Orgs>
        )}
      </PopoverBody>
    </>
  )
}
